import * as React from 'react';

import type {Section as MenuSection} from 'common/components/items-menu/types';
import {Translatable} from 'common/i18n';
import type {SidenavReact} from 'common/components/sidenav/index';
import {SvgIconType} from 'common/components/svg-icon';
import {ProjectFeatureFlag} from 'common/report/constants';

import {ScrollPosition} from './constants';

export interface SidenavAttrs {
  selected?: string;
  loading?: boolean;
  'min-adjacent-width'?: number;
  header?: SidenavHeader;
  'user-id'?: number;
  'storage-key'?: string;
  'hide-collapse-button'?: boolean;
  'fixed-size'?: boolean;
  'is-nested-dashboards-enabled'?: boolean;
  'contents-only'?: boolean;
}

export interface SidenavState {
  activeDragSectionIdx: number | null;
  collapsedSectionSet: Set<string>;
  expandedItemSet: Set<string>;
  hoveredItemValues: Set<string>;
  isOverflowMenuOpen: boolean;
  scrollPosition: ScrollPosition;
  selectedItem: SidenavSectionItem | null;
  selectedSectionIndex: number | null;
  scrolledIntoItem: string;
  sections: SidenavSection[];
}

type SidenavCtaHeader = {title?: string; actions: SidenavCta[]};

type SidenavSlotHeader = {title?: string; slot: string; collapsedSlot?: string};

export type SidenavHeader = SidenavCtaHeader | SidenavSlotHeader;

export type SidenavSection<T extends string = string> = {
  /** Key used to uniquely identify this section for persistence. */
  key?: string;
  label?: Translatable;
  items: Array<SidenavSectionItem<T>>;
  dragDropEnabled?: boolean;
  isCollapsible: boolean;
};

export type SidenavSectionItem<T = string> = {
  label: Translatable;
  value: T;
  itemId?: string;
  tooltip?: Translatable;
  userName?: string;
  userId?: number;
  userEmail?: string;

  href?: string;
  target?: string;

  overflowMenuSections?: MenuSection[];
  nestedItems?: SidenavSectionItem[];
  /**
   * Used to conditionally only show the nav item if the specified feature flag
   * is enabled for the current project.
   */
  featureFlag?: ProjectFeatureFlag;

  isNew?: boolean;

  /** Optional icon that will render to the right of the label. Will not play nice with resizable sidenav or overflowMenuSections. */
  iconRight?: SvgIconType;

  /** A tag to append to the end of the label */
  tag?: string;

  [key: string]: any;
};

export enum NestingOrder {
  First = `first`,
  Middle = `middle`,
  Last = `last`,
}

export type SidenavCta = SidenavSectionItem & {
  icon: SvgIconType;
};

export type PersistedSidenavState = {collapsedSectionSet: string[]; expandedItemSet: string[]};
export type ParsedPersistedSidenavState = Pick<SidenavState, `collapsedSectionSet` | `expandedItemSet`>;

export type SidenavItemPosition = {
  parentItem?: SidenavSectionItem;
  sectionIndex?: number;
  itemIndex?: number;
};

export type DragDropEventDetail = {
  item: SidenavSectionItem;
  sectionIndex: number;
  itemIndex: number;

  destinationPosition: SidenavItemPosition;
};

export type SidenavProps = React.ComponentProps<typeof SidenavReact>;
